import { useCallback, useEffect, useRef, useState } from "react";
import { useExtensionRuntimeTransport } from "./ExtensionRuntimeProvider";
import type { McpTransport } from "./transport";

export interface UseMcpToolOptions
{
    /**
     * Override the transport resolved from {@link ExtensionRuntimeProvider}.
     * Primarily intended for tests and advanced multi-host scenarios.
     */
    transport?: McpTransport;
}

export interface UseMcpToolResult<TReq, TRes>
{
    /**
     * Invoke the tool. Each call gets a fresh {@link AbortController} that is
     * aborted on unmount so unresolved promises cannot keep state alive after
     * the component is gone.
     */
    invoke: (req: TReq) => Promise<TRes>;
    loading: boolean;
    error: Error | undefined;
}

/**
 * Hook returning a callback that invokes an MCP tool by name through the
 * resolved {@link McpTransport}.
 *
 * `loading` and `error` track the most recent in-flight invocation. Callers
 * may also `await` the returned promise directly — failures are both rejected
 * to the caller AND surfaced via `error` so component-level UI can react.
 *
 * `invoke` is a stable reference across renders for the same `toolName` and
 * transport, making it safe to use inside `useEffect`/`useCallback` deps.
 */
export function useMcpTool<TReq, TRes>(
    toolName: string,
    opts?: UseMcpToolOptions,
): UseMcpToolResult<TReq, TRes>
{
    const transport = useExtensionRuntimeTransport(opts?.transport);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    // Track the latest controller so unmount can abort an in-flight call.
    const controllerRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);

    useEffect(() =>
    {
        mountedRef.current = true;
        return () =>
        {
            mountedRef.current = false;
            controllerRef.current?.abort();
        };
    }, []);

    const invoke = useCallback(
        async (req: TReq): Promise<TRes> =>
        {
            const controller = new AbortController();
            controllerRef.current?.abort();
            controllerRef.current = controller;

            setLoading(true);
            setError(undefined);

            try
            {
                const result = await transport.invokeTool<TReq, TRes>(
                    toolName,
                    req,
                    controller.signal,
                );
                if (mountedRef.current && !controller.signal.aborted)
                {
                    setLoading(false);
                }
                return result;
            }
            catch (err)
            {
                const wrapped = err instanceof Error ? err : new Error(String(err));
                if (mountedRef.current && !controller.signal.aborted)
                {
                    setError(wrapped);
                    setLoading(false);
                }
                throw wrapped;
            }
        },
        [transport, toolName],
    );

    return { invoke, loading, error };
}
